import { AES, enc } from "crypto-js";
import { BASE_URL } from "../../../screens/Dashboard/CommonApi";
import { amountToIndianWords } from "../../../utils/amountInWords";
import { shouldShowDiscountColumn } from "../../../utils/discountColumn";
import { getDocumentPrintConfig } from "./documentPrintConfig";

export const safeText = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

export const toNumber = (value) => {
  const next = Number(String(value || "0").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(next) ? next : 0;
};

const flagEnabled = (value) => {
  if (typeof value === "boolean") return value;
  const normalized = String(value || "").trim().toLowerCase();
  return ["yes", "true", "1", "on"].includes(normalized);
};

export const formatMoney = (value) =>
  `₹ ${toNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

export const firstValue = (source = {}, keys = [], fallback = "") => {
  const key = keys.find((item) => source[item] !== undefined && source[item] !== null && source[item] !== "");
  return key ? source[key] : fallback;
};

export const imageUrl = (value) => {
  if (!value) return "";
  if (/^data:|^https?:\/\//i.test(value)) return value;
  return encodeURI(`https://documents.zarpsoftware.in/zarp_images/${value}`);
};

const blobToDataUrl = (blob, fallback = "") =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result || fallback);
    reader.onerror = () => resolve(fallback);
    reader.readAsDataURL(blob);
  });

export const imageToDataUrl = async (src, profileAssetType = "") => {
  if (!src || /^data:/i.test(src)) return src || "";
  try {
    const response = await fetch(src, { mode: "cors" });
    if (response.ok) return await blobToDataUrl(await response.blob(), src);
  } catch (error) {
    // The document host does not consistently allow canvas-safe CORS reads.
  }

  if (profileAssetType) {
    try {
      const jwtToken = localStorage.getItem("jwtToken");
      const response = await fetch(
        `${BASE_URL}/fetchProfileAsset/${profileAssetType}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({
            uniqueId: decryptUniqueId(),
            userId: localStorage.getItem("userId"),
          }),
        },
      );
      if (response.ok) {
        return await blobToDataUrl(await response.blob(), src);
      }
    } catch (error) {
      // Keep the original URL so browser printing can still render the image.
    }
  }

  return src;
};

export const waitForImages = async (element) => {
  if (!element) return;
  const images = Array.from(element.querySelectorAll("img"));
  await Promise.all(
    images.map(
      (image) =>
        new Promise((resolve) => {
          const timeout = window.setTimeout(resolve, 2000);
          const finish = () => {
            window.clearTimeout(timeout);
            resolve();
          };
          if (image.complete && image.naturalWidth !== 0) {
            finish();
            return;
          }
          image.onload = finish;
          image.onerror = finish;
        }),
    ),
  );
};

const decryptUniqueId = () => {
  const encryptedUniqueId = localStorage.getItem("uniqueId1");
  if (!encryptedUniqueId) return "";
  return AES.decrypt(encryptedUniqueId, "1234567812345678").toString(enc.Utf8);
};

const authedPost = async (endpoint, body = {}) => {
  const jwtToken = localStorage.getItem("jwtToken");
  const response = await fetch(BASE_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
    body: JSON.stringify({ uniqueId: decryptUniqueId(), ...body }),
  });
  return response.json();
};

const normalizeRows = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  return Object.values(value).find(Array.isArray) || [];
};

const normalizeRecord = (value) => {
  if (Array.isArray(value)) return value[0] || {};
  if (!value || typeof value !== "object") return {};
  if (value.responseMessage && value.responseMessage !== value) {
    return normalizeRecord(value.responseMessage);
  }
  return value;
};

export const fetchDocumentPrintContext = async (row = {}) => {
  const userId = localStorage.getItem("userId");
  const [profileResult, gstResult, bankResult, termsResult] = await Promise.allSettled([
    authedPost("/fetchUserProfile", userId ? { userId } : {}),
    authedPost("/fetchGst"),
    authedPost("/fetchBankForPdf"),
    authedPost("/fetchTermsAndConditions"),
  ]);

  const company =
    profileResult.status === "fulfilled" && profileResult.value?.status === "success"
      ? profileResult.value.responseMessage || {}
      : {};
  const gst =
    gstResult.status === "fulfilled" && gstResult.value?.status === "success"
      ? normalizeRows(gstResult.value.responseMessage)[0] || {}
      : {};
  let bank =
    bankResult.status === "fulfilled" && bankResult.value?.status === "success"
      ? normalizeRecord(bankResult.value.responseMessage)
      : {};

  if (!bank.bankName && !bank.accountNumber && !bank.upiId) {
    const allBanksResult = await authedPost("/fetchBanks").catch(() => null);
    const banks = normalizeRows(allBanksResult?.responseMessage);
    bank =
      banks.find((item) => String(item.primaryAccount).toUpperCase() === "YES") ||
      banks[0] ||
      bank;
  }
  const terms =
    termsResult.status === "fulfilled" && termsResult.value?.status === "success"
      ? termsResult.value.responseMessage || {}
      : {};

  let party = {};
  const customerId = row.customerId;
  const vendorId = row.vendorId;
  if (customerId) {
    const customerResult = await authedPost(`/fetchCustomerById/${customerId}`).catch(() => null);
    if (customerResult?.status === "success") {
      const rows = normalizeRows(customerResult.responseMessage);
      party = rows[0] || customerResult.responseMessage || {};
    }
  } else if (vendorId) {
    const vendorResult = await authedPost(`/fetchVendorById/${vendorId}`).catch(() => null);
    if (vendorResult?.status === "success") {
      const rows = normalizeRows(vendorResult.responseMessage);
      party = rows[0] || vendorResult.responseMessage || {};
    }
  }

  let documentRows = [];
  if (row.invoiceNumber) {
    const documentFetchByType = {
      pro_forma: `/fetchProFormaByInvoiceNumber/${row.invoiceNumber}`,
      proforma: `/fetchProFormaByInvoiceNumber/${row.invoiceNumber}`,
    };
    const documentEndpoint = documentFetchByType[row.documentType || row.typeInvoice || row.type];
    if (documentEndpoint) {
      const documentResult = await authedPost(documentEndpoint).catch(() => null);
      if (documentResult?.status === "success") {
        documentRows = normalizeRows(documentResult.responseMessage);
      }
    }
  }

  return { company, gst, bank, party, terms, documentRows };
};

const splitList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  return value.split(",").map((item) => item.trim());
};

const buildItems = (row = {}) => {
  const direct = [row.items, row.itemRows, row.lineItems, row.itemDetails, row.products].find(Array.isArray);
  if (direct) return direct;

  const names = splitList(row.itemsDetails || row.itemDetails || row.item_details || row.itemName || row.expenseAccount);
  const qtys = splitList(row.quantity);
  const rates = splitList(row.rate);
  const amounts = splitList(row.amount || row.value);
  const hsn = splitList(row.hsnSac || row.hsn_sac || row.hsnCode);
  const taxes = splitList(row.tax);
  const discounts = splitList(row.discount || row.rowDiscount);
  const max = Math.max(names.length, qtys.length, rates.length, amounts.length, hsn.length, taxes.length, discounts.length, 1);

  return Array.from({ length: max }).map((_, index) => ({
    name: names[index] || row.itemsDetails || row.itemName || row.expenseAccount || row.reference || "Document line item",
    quantity: qtys[index] || row.quantity || 1,
    rate: rates[index] || row.rate || row.total || row.amount,
    amount: amounts[index] || row.amount || row.total,
    hsn: hsn[index] || row.hsnSac || row.hsnCode,
    taxRate: taxes[index] || row.tax,
    discount: discounts[index] || row.discount || row.rowDiscount,
  }));
};

const joinAddress = (...parts) => parts.filter(Boolean).join(", ");

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const sumItemAmounts = (items = []) =>
  items.reduce((sum, item) => {
    const quantity = toNumber(firstValue(item, ["quantity", "qty"], 1));
    const rate = toNumber(firstValue(item, ["rate", "price"], 0));
    const amount = firstValue(item, ["amount", "total"], "");
    return sum + (hasValue(amount) ? toNumber(amount) : quantity * rate);
  }, 0);

const calculateGstBreakup = (items = [], sourceRow = {}) => {
  const storedCgst = toNumber(sourceRow.cgst);
  const storedSgst = toNumber(sourceRow.sgst);
  const storedIgst = toNumber(sourceRow.igst);

  if (storedCgst !== 0 || storedSgst !== 0 || storedIgst !== 0) {
    return {
      cgst: storedCgst,
      sgst: storedSgst,
      igst: storedIgst,
      rows: [
        storedCgst !== 0 ? { label: "CGST", amount: storedCgst } : null,
        storedSgst !== 0 ? { label: "SGST", amount: storedSgst } : null,
        storedIgst !== 0 ? { label: "IGST", amount: storedIgst } : null,
      ].filter(Boolean),
    };
  }

  const hasIgstItems = items.some((item) => toNumber(item.igst) !== 0);
  const gstRowsByLabel = {};

  items.forEach((item) => {
    const taxRate = toNumber(firstValue(item, ["taxRate", "tax"], sourceRow.tax || ""));
    if (taxRate === 0) return;

    const taxableAmount = hasValue(firstValue(item, ["amount", "total"], ""))
      ? toNumber(firstValue(item, ["amount", "total"], 0))
      : toNumber(firstValue(item, ["quantity", "qty"], 1)) * toNumber(firstValue(item, ["rate", "price"], 0));
    const gstAmount = (taxableAmount * taxRate) / 100;
    if (gstAmount === 0) return;

    const entries = hasIgstItems
      ? [{ label: `IGST(${taxRate}%)`, type: "igst", amount: gstAmount }]
      : [
          { label: `CGST(${taxRate / 2}%)`, type: "cgst", amount: gstAmount / 2 },
          { label: `SGST(${taxRate / 2}%)`, type: "sgst", amount: gstAmount / 2 },
        ];

    entries.forEach((entry) => {
      gstRowsByLabel[entry.label] = gstRowsByLabel[entry.label] || { ...entry, amount: 0 };
      gstRowsByLabel[entry.label].amount += entry.amount;
    });
  });

  const rows = Object.values(gstRowsByLabel);
  return {
    cgst: rows.filter((row) => row.type === "cgst").reduce((sum, row) => sum + row.amount, 0),
    sgst: rows.filter((row) => row.type === "sgst").reduce((sum, row) => sum + row.amount, 0),
    igst: rows.filter((row) => row.type === "igst").reduce((sum, row) => sum + row.amount, 0),
    rows,
  };
};

export const resolveDocumentSubtotal = (row = {}, items = []) => {
  const explicitSubtotal = firstValue(row, ["subtotal", "subTotal", "sub_total"], "");
  if (hasValue(explicitSubtotal)) return explicitSubtotal;

  const taxableAmount = firstValue(row, ["taxableAmount", "taxable_amount"], "");
  if (hasValue(taxableAmount)) return taxableAmount;

  const itemSubtotal = sumItemAmounts(items);
  if (itemSubtotal !== 0) return itemSubtotal;

  return firstValue(row, ["total", "grandTotal", "amount", "creditAmount", "expenseAmount"], 0);
};

const resolveDocumentTitle = (config, isGstApplicable) => {
  if (typeof config.documentTitle === "function") return config.documentTitle({ isGstApplicable });
  return config.documentTitle;
};

export const buildDocumentPrintModel = ({
  row = {},
  documentType = "invoice",
  config: configOverrides = {},
  context = {},
  existingModel,
} = {}) => {
  if (existingModel?.company && (existingModel?.party || existingModel?.customer)) {
    return normalizeExistingPrintModel(existingModel, documentType, configOverrides);
  }

  const config = getDocumentPrintConfig(documentType, configOverrides);
  const sourceRows = Array.isArray(context.documentRows) && context.documentRows.length
    ? context.documentRows
    : [];
  const sourceRow = sourceRows[0] || row;
  const companyData = context.company || {};
  const gstData = context.gst || {};
  const partyData = context.party || {};
  const bankData = normalizeRecord(context.bank);
  const isGstRegistrationActive = flagEnabled(firstValue(gstData, ["businessRegistered"], "NO"));
  const companyGstin = firstValue(
    gstData,
    ["gstin", "gstNumber"],
    firstValue(companyData, ["gstNumber", "gstin", "gstInUin"], firstValue(sourceRow, ["gstNumber", "companyGstin", "organizationGSTNo"], "")),
  );
  const partyGstin = firstValue(
    sourceRow,
    ["customerGstNumber", "custmorGstNo", "customerGSTNo", "vendorGstNumber"],
    firstValue(partyData, ["gstin", "gstInUin", "gstNumber", "gstinUIN"], ""),
  );
  const hasStoredTax = ["cgst", "sgst", "igst", "tax", "taxInAmount"].some((key) => toNumber(sourceRow[key]) !== 0);
  const explicitTaxFlag = firstValue(sourceRow, ["taxCheck", "taxColumn", "isGstApplicable", "isGstInvoice"], null);
  const isGstApplicable =
    isGstRegistrationActive &&
    (explicitTaxFlag !== null
      ? flagEnabled(explicitTaxFlag)
      : Boolean(companyGstin || partyGstin || hasStoredTax));
  const items = sourceRows.length ? sourceRows : buildItems(row);

  const subtotal = resolveDocumentSubtotal(sourceRow, items);
  const gstBreakup = calculateGstBreakup(items, sourceRow);
  const total = firstValue(sourceRow, ["total", "grandTotal", "amount", "creditAmount", "expenseAmount"], subtotal);
  const amountInWords = firstValue(sourceRow, ["amountInWords", "totalInWords"], "") || amountToIndianWords(total);

  return {
    documentType,
    title: resolveDocumentTitle(config, isGstApplicable),
    filePrefix: config.filePrefix,
    isGstApplicable,
    showHsn: isGstApplicable && (items.some((item) => item.hsn || item.hsnCode || item.hsnSac) || sourceRow.hsnCheck === "YES"),
    showDiscount: shouldShowDiscountColumn(sourceRow, items),
    labels: {
      number: config.numberLabel,
      date: config.dateLabel,
      due: config.dueLabel,
      party: config.partyLabel,
    },
    company: {
      name: firstValue(companyData, ["companyName", "organizationName", "name"], firstValue(sourceRow, ["companyName", "organizationName"], "Company")),
      logoUrl: imageUrl(firstValue(companyData, ["logo", "imageName", "companyLogo", "logoName"], firstValue(sourceRow, ["imageName", "logo"], ""))),
      signatureUrl: imageUrl(firstValue(companyData, ["signImage", "sign_image", "signature", "signatureImage"], "")),
      stampUrl: imageUrl(firstValue(companyData, ["stampImage", "stamp_image", "stamp", "companyStamp", "stampPhoto"], "")),
      gstin: isGstApplicable ? companyGstin : "",
      phone: firstValue(companyData, ["contact", "phone", "mobile"], firstValue(sourceRow, ["orgMob1"], "")),
      email: firstValue(companyData, ["email"], ""),
      state: firstValue(companyData, ["state"], firstValue(sourceRow, ["state"], "")),
      address: joinAddress(
        firstValue(companyData, ["address1", "organizationStreetName"], ""),
        firstValue(companyData, ["address2", "organizationAddress"], ""),
        firstValue(companyData, ["city"], ""),
        firstValue(companyData, ["state"], ""),
        firstValue(companyData, ["zipcode", "pinCode"], ""),
      ),
    },
    party: {
      name: firstValue(sourceRow, ["customerName", "vendorName", "payee", "name"], firstValue(partyData, ["customerName", "vendorName", "companyName", "name"], "Party")),
      address: joinAddress(
        firstValue(partyData, ["billAddress1", "address1", "vendorAddress"], firstValue(sourceRow, ["billingAddress", "deliveryAddress", "deliverTo"], "")),
        firstValue(partyData, ["billAddress2", "address2"], ""),
        firstValue(partyData, ["billCity", "city"], ""),
        firstValue(partyData, ["billState", "state"], firstValue(sourceRow, ["state"], "")),
        firstValue(partyData, ["billZipcode", "zipcode"], ""),
      ),
      gstin: isGstApplicable ? partyGstin : "",
      state: firstValue(partyData, ["billState", "state"], firstValue(sourceRow, ["state"], "")),
      phone: firstValue(partyData, ["billPhone", "phone", "mobile"], firstValue(sourceRow, ["phone", "mobile"], "")),
      email: firstValue(partyData, ["email"], firstValue(sourceRow, ["email", "customerEmail", "vendorEmail"], "")),
    },
    meta: {
      number: firstValue(sourceRow, config.numberKeys, sourceRow.id || ""),
      date: firstValue(sourceRow, config.dateKeys, ""),
      due: firstValue(sourceRow, config.dueKeys, ""),
      expiryDate: firstValue(sourceRow, ["expiryDate", "validTill", "dueDate"], ""),
      terms: firstValue(sourceRow, ["paymentTerms", "terms", "termsAndCondition"], ""),
      status: firstValue(sourceRow, ["status", "paymentStatus"], "Draft"),
      reference: firstValue(sourceRow, ["reference", "orderNumber", "originalInvoiceNumber"], ""),
      category: firstValue(sourceRow, ["expenseAccount", "category"], ""),
    },
    subject: firstValue(sourceRow, ["subject", "quotationSubject", "quoteSubject"], ""),
    details: firstValue(sourceRow, ["otherDetails", "details", "terms"], ""),
    items: items.map((item, index) => ({
      id: `${item.id || item.itemId || item.name || index}-${index}`,
      serial: index + 1,
      name: firstValue(item, ["itemsDetails", "itemDetails", "items", "itemName", "name", "description"], ""),
      hsn: firstValue(item, ["hsn", "hsnCode", "hsnSac"], ""),
      quantity: firstValue(item, ["quantity", "qty"], 1),
      rate: firstValue(item, ["rate", "price"], 0),
      discount: firstValue(item, ["discount", "rowDiscount"], ""),
      discountType: firstValue(item, ["discountType"], sourceRow.totalDiscountType || ""),
      taxRate: firstValue(item, ["taxRate", "tax"], sourceRow.tax || ""),
      amount: firstValue(item, ["amount", "total"], toNumber(firstValue(item, ["quantity", "qty"], 1)) * toNumber(firstValue(item, ["rate", "price"], 0))),
    })),
    totals: {
      subtotal,
      discount: firstValue(sourceRow, ["totalDiscount", "discount", "overallDiscount"], ""),
      taxableAmount: firstValue(sourceRow, ["taxableAmount", "taxable_amount"], subtotal),
      adjustment: firstValue(sourceRow, ["adjustment"], ""),
      adjustmentLabel: firstValue(sourceRow, ["adjustmentLabel"], "Round Off"),
      cgst: gstBreakup.cgst,
      sgst: gstBreakup.sgst,
      igst: gstBreakup.igst,
      gstRows: gstBreakup.rows,
      grandTotal: total,
      paidAmount: firstValue(sourceRow, ["paidAmount", "payment"], ""),
      balanceDue: firstValue(sourceRow, ["balance", "receivables"], ""),
      amountInWords,
    },
    notes: firstValue(sourceRow, ["customerNote", "customerNotes", "notes", "note"], "Thank you for your business."),
    termsAndCondition: firstValue(
      sourceRow,
      ["termsAndCondition", "terms_and_condition", "termsAndConditions"],
      firstValue(context.terms || {}, ["termsAndCondition", "terms", "terms_and_condition"], ""),
    ),
    bank: {
      bankName: firstValue(bankData, ["bankName", "bank", "branch"], firstValue(sourceRow, ["bankName", "bank", "branch"], "")),
      branchName: firstValue(bankData, ["branchName"], firstValue(sourceRow, ["branchName"], "")),
      accountName: firstValue(bankData, ["name", "accountName"], firstValue(sourceRow, ["accountName", "name"], "")),
      accountNumber: firstValue(bankData, ["accountNumber", "accountNo"], firstValue(sourceRow, ["accountNumber", "accountNo"], "")),
      ifsc: firstValue(bankData, ["ifsc", "ifscCode"], firstValue(sourceRow, ["ifsc", "ifscCode"], "")),
      upiId: firstValue(bankData, ["upiId", "upi"], firstValue(sourceRow, ["upiId", "upi"], "")),
      currency: firstValue(bankData, ["currency"], firstValue(sourceRow, ["currency"], "")),
      accountType: firstValue(bankData, ["accountType", "type"], firstValue(sourceRow, ["accountType", "type"], "")),
    },
  };
};

export const normalizeExistingPrintModel = (model = {}, documentType = "invoice", overrides = {}) => {
  const config = getDocumentPrintConfig(documentType, overrides);
  const party = model.party || model.customer || {};
  const explicitTaxFlag = firstValue(model, ["taxCheck", "taxColumn", "isGstInvoice", "isGstApplicable"], null);
  const isGstApplicable = explicitTaxFlag !== null ? flagEnabled(explicitTaxFlag) : false;
  const subtotal = hasValue(model.totals?.subtotal)
    ? model.totals.subtotal
    : resolveDocumentSubtotal({}, model.items || []);
  const grandTotal = firstValue(model.totals || {}, ["grandTotal", "total"], subtotal);
  const gstBreakup = calculateGstBreakup(model.items || [], model.totals || {});
  return {
    documentType,
    title: model.title || resolveDocumentTitle(config, isGstApplicable),
    filePrefix: config.filePrefix,
    isGstApplicable,
    showHsn: Boolean(model.showHsn),
    showDiscount: shouldShowDiscountColumn(model, model.items || []),
    labels: {
      number: config.numberLabel,
      date: config.dateLabel,
      due: config.dueLabel,
      party: config.partyLabel,
    },
    company: {
      ...(model.company || {}),
      signatureUrl: imageUrl(firstValue(model.company || {}, ["signatureUrl", "signImage", "sign_image", "signature", "signatureImage"], "")),
      stampUrl: imageUrl(firstValue(model.company || {}, ["stampUrl", "stampImage", "stamp_image", "stamp", "companyStamp", "stampPhoto"], "")),
    },
    party,
    meta: {
      number: model.invoice?.number || model.meta?.number,
      date: model.invoice?.date || model.meta?.date,
      due: model.invoice?.dueDate || model.meta?.due,
      terms: model.invoice?.terms || model.meta?.terms,
      status: model.invoice?.paymentStatus || model.status?.label || model.meta?.status,
      reference: model.invoice?.orderNumber || model.meta?.reference,
    },
    subject: model.subject || model.meta?.subject || "",
    details: model.details || model.otherDetails || model.meta?.details || model.meta?.terms || "",
    items: model.items || [],
    totals: {
      subtotal,
      discount: model.totals?.discount,
      taxableAmount: model.totals?.taxableAmount || subtotal,
      adjustment: model.totals?.adjustment,
      adjustmentLabel: model.totals?.adjustmentLabel || "Round Off",
      cgst: gstBreakup.cgst,
      sgst: gstBreakup.sgst,
      igst: gstBreakup.igst,
      gstRows: model.totals?.gstRows || gstBreakup.rows,
      grandTotal,
      paidAmount: model.totals?.paidAmount,
      balanceDue: model.totals?.balanceDue,
      amountInWords: model.totals?.amountInWords || amountToIndianWords(grandTotal),
    },
    notes: model.notes,
    termsAndCondition: model.termsAndCondition,
    bank: {
      bankName: firstValue(model.bank || {}, ["bankName", "bank", "branch"], firstValue(model, ["bankName", "bank", "branch"], "")),
      branchName: firstValue(model.bank || {}, ["branchName"], firstValue(model, ["branchName"], "")),
      accountName: firstValue(model.bank || {}, ["accountName", "name"], firstValue(model, ["accountName", "name"], "")),
      accountNumber: firstValue(model.bank || {}, ["accountNumber", "accountNo"], firstValue(model, ["accountNumber", "accountNo"], "")),
      ifsc: firstValue(model.bank || {}, ["ifsc", "ifscCode"], firstValue(model, ["ifsc", "ifscCode"], "")),
      upiId: firstValue(model.bank || {}, ["upiId", "upi"], firstValue(model, ["upiId", "upi"], "")),
      currency: firstValue(model.bank || {}, ["currency"], firstValue(model, ["currency"], "")),
      accountType: firstValue(model.bank || {}, ["accountType", "type"], firstValue(model, ["accountType", "type"], "")),
    },
  };
};

export const documentFileName = (model = {}) => {
  const safe = (value) => String(value || "document").replace(/[^a-z0-9-_]+/gi, "_").replace(/^_+|_+$/g, "");
  return `${safe(model.filePrefix || "Document")}_${safe(model.meta?.number)}.pdf`;
};
